import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { engineerService, EngineerMe } from '../../services/engineerService';
import { PageHeader, Button, FormField, TextInput } from '../../components/ui';

const EngineerProfile: React.FC = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<EngineerMe>({
    queryKey: ['engineer', 'me'],
    queryFn: () => engineerService.me(),
  });

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [baseAddress, setBaseAddress] = useState('');
  const [basePostcode, setBasePostcode] = useState('');
  const [serviceRadius, setServiceRadius] = useState<string>('');
  const [maxJobs, setMaxJobs] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setName(data.name || '');
    setPhone(data.phone || '');
    setAddress(data.address || '');
    setBaseAddress(data.baseAddress || '');
    setBasePostcode(data.basePostcode || '');
    setServiceRadius(
      typeof data.serviceRadiusMiles === 'number' ? String(data.serviceRadiusMiles) : '',
    );
    setMaxJobs(typeof data.maxJobsPerDay === 'number' ? String(data.maxJobsPerDay) : '');
  }, [data]);

  const dirty = useMemo(() => {
    if (!data) return false;
    return (
      (data.name || '') !== name ||
      (data.phone || '') !== phone ||
      (data.address || '') !== address ||
      (data.baseAddress || '') !== baseAddress ||
      (data.basePostcode || '') !== basePostcode ||
      String(data.serviceRadiusMiles ?? '') !== serviceRadius ||
      String(data.maxJobsPerDay ?? '') !== maxJobs
    );
  }, [data, name, phone, address, baseAddress, basePostcode, serviceRadius, maxJobs]);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await engineerService.updateMe({
        name: name.trim() || undefined,
        phone: phone.trim() || undefined,
        address: address.trim() || undefined,
        baseAddress: baseAddress.trim() || undefined,
        basePostcode: basePostcode.trim() || undefined,
        serviceRadiusMiles: serviceRadius ? parseInt(serviceRadius, 10) : null,
        maxJobsPerDay: maxJobs ? parseInt(maxJobs, 10) : null,
      });
      setMessage('Saved');
      qc.invalidateQueries({ queryKey: ['engineer', 'me'] });
    } catch (err: any) {
      setMessage(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-4 space-y-6">
      <PageHeader
        title="Profile"
        subtitle="Your contact details and where you typically start your day."
      />

      {isLoading && <div className="text-sm text-slate-500">Loading…</div>}

      <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <h2 className="text-sm font-semibold text-slate-900">Personal details</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <FormField label="Full name">
            <TextInput value={name} onChange={(e) => setName(e.target.value)} />
          </FormField>
          <FormField label="Phone">
            <TextInput
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="07…"
            />
          </FormField>
        </div>
        <FormField label="Home address">
          <TextInput
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Optional"
          />
        </FormField>
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <h2 className="text-sm font-semibold text-slate-900">Work base</h2>
        <p className="text-xs text-slate-500">
          Where your working day typically starts. Used for smart route planning and
          job assignment.
        </p>
        <div className="grid sm:grid-cols-[1fr,160px] gap-3">
          <FormField label="Base address">
            <TextInput
              value={baseAddress}
              onChange={(e) => setBaseAddress(e.target.value)}
            />
          </FormField>
          <FormField label="Postcode">
            <TextInput
              value={basePostcode}
              onChange={(e) => setBasePostcode(e.target.value.toUpperCase())}
            />
          </FormField>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <FormField label="Service radius (miles)" help="Max distance admins can auto-assign you.">
            <TextInput
              type="number"
              min={0}
              max={200}
              value={serviceRadius}
              onChange={(e) => setServiceRadius(e.target.value)}
            />
          </FormField>
          <FormField
            label="Max jobs per day"
            help="Cap for auto-assignment. Leave empty for no limit."
          >
            <TextInput
              type="number"
              min={0}
              max={20}
              value={maxJobs}
              onChange={(e) => setMaxJobs(e.target.value)}
            />
          </FormField>
        </div>
      </section>

      <div className="flex items-center justify-end gap-2">
        {message === 'Saved' && (
          <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
            <CheckCircleIcon className="w-4 h-4" /> Saved
          </span>
        )}
        {message && message !== 'Saved' && (
          <span className="text-xs text-red-700">{message}</span>
        )}
        <Button onClick={save} loading={saving} disabled={!dirty}>
          Save changes
        </Button>
      </div>
    </div>
  );
};

export default EngineerProfile;
